import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import './SleepWheel.css';

interface SleepEntry {
  id: string;
  baby_id: string;
  start_time: string;
  end_time: string | null;
  created_at: string;
  is_ongoing: boolean;
  duration_minutes: number | null;
}

interface PredictedSlot {
  predicted_start_time: string;
  predicted_end_time: string;
  slot_type: 'nap' | 'night_sleep';
  confidence_score?: number;
}

interface SleepWheelProps {
  sleepEntries: SleepEntry[];
  ongoingSleep: SleepEntry | null;
  onSleepClick?: (entry: SleepEntry) => void;
  use24h?: boolean;
  nextPrediction?: {
    slot: PredictedSlot;
    displayMode: 'waking' | 'should-be-sleeping' | 'future';
  } | null;
  onPredictionClick?: () => void;
  isLoadingPrediction?: boolean;
}

const SleepWheel: React.FC<SleepWheelProps> = ({
  sleepEntries,
  ongoingSleep,
  onSleepClick,
  use24h = false,
  nextPrediction,
  onPredictionClick,
  isLoadingPrediction = false
}) => {
  // Configuration: Time window to display
  const TOTAL_HOURS = 12;  // Total hours in the wheel's time period
  const MARGIN_HOURS = 0;  // Margin to exclude near current time
  const VISIBLE_HOURS = TOTAL_HOURS - MARGIN_HOURS;  // Actual visible window

  const [currentTime, setCurrentTime] = useState(new Date());
  const [zoomedEntry, setZoomedEntry] = useState<SleepEntry | null>(null);
  const [viewBox, setViewBox] = useState("0 0 400 400");

  // Update current time every second for live indicator
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Dynamically adjust zoom when the zoomed entry's times are modified
  useEffect(() => {
    if (!zoomedEntry) return;

    // Find the current version of the zoomed entry in sleepEntries
    const updatedEntry = sleepEntries.find(e => e.id === zoomedEntry.id);

    if (updatedEntry) {
      // Check if times have changed
      if (updatedEntry.start_time !== zoomedEntry.start_time ||
          updatedEntry.end_time !== zoomedEntry.end_time) {
        // Update the zoomed entry reference
        setZoomedEntry(updatedEntry);
        // Recalculate viewBox to keep the adjusted entry visible
        const newViewBox = calculateZoomViewBox(updatedEntry);
        setViewBox(newViewBox);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleepEntries, zoomedEntry]);

  // Convert time to angle (12/00 at top, rotating clockwise)
  const timeToAngle = (date: Date): number => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const timeInHours = hours + minutes / 60;
    // Convert to degrees, starting from top (12 o'clock position = 270 degrees in our coordinate system)
    // Subtract 90 to rotate the wheel so 0 hours is at top
    return (timeInHours / TOTAL_HOURS) * 360 + 270;
  };

  // Convert polar coordinates to cartesian for SVG
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    };
  };

  // Create SVG arc path
  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);

    // Calculate the angular difference, accounting for wrapping around 360 degrees
    let angleDiff = endAngle - startAngle;
    if (angleDiff < 0) {
      angleDiff += 360;
    }

    // Prevent drawing arcs that are too small (less than 0.1 degrees) or invalid
    if (angleDiff < 0.1) {
      return ""; // Return empty path for invalid/tiny arcs
    }

    const largeArcFlag = angleDiff > 180 ? "1" : "0";

    return [
      "M", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
  };

  // Filter to get sleep entries from the visible time window
  const getTodaysSleep = (): SleepEntry[] => {
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - VISIBLE_HOURS * 60 * 60 * 1000);

    return sleepEntries.filter(entry => {
      const endTime = entry.end_time ? new Date(entry.end_time) : null;

      // Exclude entries that overlap with the margin period
      // (entries that end or are ongoing within the visible time window)
      if (entry.is_ongoing) {
        // Always include ongoing sleep
        return true;
      } else if (endTime) {
        // Include completed entries that ended within the time window
        return endTime >= cutoffTime;
      }

      return false;
    });
  };

  // Calculate total sleep for today
  const getTotalSleepMinutes = (): number => {
    const todaysSleep = getTodaysSleep();
    let total = 0;

    todaysSleep.forEach(entry => {
      if (entry.duration_minutes !== null) {
        total += entry.duration_minutes;
      }
    });

    // Add ongoing sleep duration
    if (ongoingSleep) {
      const startTime = new Date(ongoingSleep.start_time);
      const now = new Date();
      const ongoingMinutes = Math.floor((now.getTime() - startTime.getTime()) / (1000 * 60));
      total += ongoingMinutes;
    }

    return total;
  };

  const formatTotalSleep = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDuration = (minutes: number | null): string => {
    if (minutes === null) return 'Ongoing';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatHourLabel = (hour: number): string => {
    if (use24h) {
      return hour.toString().padStart(2, '0');
    }
    // 12h AM/PM format
    if (hour === 0) return '12AM';
    if (hour < 12) return `${hour}AM`;
    if (hour === 12) return '12PM';
    return `${hour - 12}PM`;
  };

  const formatTimeLabel = (date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (use24h) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    // 12h format with AM/PM
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const centerX = 200;
  const centerY = 200;
  const radius = 140;
  const strokeWidth = 30;
  const gapDegrees = 0.5; // Minimal gap to prevent visual overlap with current time marker

  const todaysSleep = getTodaysSleep();
  const totalSleepMinutes = getTotalSleepMinutes();

  // Get current time angle for indicator
  const currentAngle = timeToAngle(currentTime);

  // Calculate the visible arc range (excluding the margin)
  // The visible arc goes from (current time - VISIBLE_HOURS) to current time
  const visibleArcStartAngle = currentAngle - (VISIBLE_HOURS / TOTAL_HOURS) * 360;
  const visibleArcEndAngle = currentAngle;

  // Helper to check if an angle is too close to current time
  const shouldClipArc = (endAngle: number): boolean => {
    let diff = currentAngle - endAngle;
    // Normalize difference to -180 to 180 range
    while (diff > 180) diff -= 360;
    while (diff < -180) diff += 360;
    // If end angle is within gap degrees before current time (counterclockwise), clip it
    return diff > 0 && diff < gapDegrees;
  };

  // Adjust end angle to respect gap
  const adjustEndAngle = (endAngle: number, startAngle: number): number => {
    // Normalize end angle to be in range relative to current angle
    let normalizedEnd = endAngle;
    while (normalizedEnd > currentAngle) normalizedEnd -= 360;
    while (normalizedEnd < currentAngle - 360) normalizedEnd += 360;

    if (shouldClipArc(normalizedEnd)) {
      const clippedAngle = currentAngle - gapDegrees;

      // Ensure the clipped end angle is still after the start angle
      let normalizedStart = startAngle;
      while (normalizedStart > currentAngle) normalizedStart -= 360;
      while (normalizedStart < currentAngle - 360) normalizedStart += 360;

      // If clipping would make end before start, don't draw the arc
      if (clippedAngle <= normalizedStart) {
        return startAngle - 1; // Signal to skip this arc (will be caught by describeArc)
      }

      return clippedAngle;
    }
    return normalizedEnd;
  };

  // Adjust start angle to not extend into margin area
  const adjustStartAngle = (startAngle: number): number => {
    // Normalize angles to be in range relative to current angle
    let normalizedStart = startAngle;
    let normalizedWindowStart = visibleArcStartAngle;

    // Normalize both to be in the range [currentAngle - 360, currentAngle]
    while (normalizedStart > currentAngle) normalizedStart -= 360;
    while (normalizedStart < currentAngle - 360) normalizedStart += 360;
    while (normalizedWindowStart > currentAngle) normalizedWindowStart -= 360;
    while (normalizedWindowStart < currentAngle - 360) normalizedWindowStart += 360;

    if (normalizedStart < normalizedWindowStart) {
      // Return normalized window start to keep angles in same range
      return normalizedWindowStart;
    }
    return startAngle;
  };

  // Calculate opacity based on how long ago the sleep ended
  const calculateOpacity = (endTime: Date): number => {
    const now = new Date();
    const hoursAgo = (now.getTime() - endTime.getTime()) / (1000 * 60 * 60);

    // Map 0 hours ago -> 1.0 opacity, VISIBLE_HOURS ago -> 0.15 opacity
    const minOpacity = 0.15;
    const maxOpacity = 1.0;
    const opacity = maxOpacity - (hoursAgo / VISIBLE_HOURS) * (maxOpacity - minOpacity);

    // Clamp to min/max range
    return Math.max(minOpacity, Math.min(maxOpacity, opacity));
  };

  // Calculate viewBox for zooming into a sleep entry
  const calculateZoomViewBox = (entry: SleepEntry): string => {
    const startTime = new Date(entry.start_time);
    const endTime = entry.end_time ? new Date(entry.end_time) : currentTime;

    const startAngle = timeToAngle(startTime);
    const endAngle = timeToAngle(endTime);

    // Calculate the angular extent of the arc
    let arcExtent = endAngle - startAngle;
    if (arcExtent < 0) arcExtent += 360;
    if (arcExtent > 360) arcExtent = 360;

    // Calculate the center angle of the arc
    let centerAngle = (startAngle + endAngle) / 2;

    // Handle wrap-around if needed
    if (Math.abs(endAngle - startAngle) > 180) {
      centerAngle = ((startAngle + endAngle + 360) / 2) % 360;
    }

    // Convert center angle to cartesian coordinates on the arc
    const centerPos = polarToCartesian(centerX, centerY, radius, centerAngle);

    // Dynamic zoom factor based on arc extent
    // Add padding factor to ensure the arc doesn't touch edges
    const paddingFactor = 1.25; // 25% padding around the arc
    const arcExtentWithPadding = arcExtent * paddingFactor;

    // Calculate required viewBox size to show the arc
    // The arc spans arcExtentWithPadding degrees on a circle of radius ~140
    // We need to show a chord length approximately: 2 * radius * sin(arcExtent/2)
    const arcRadians = (arcExtentWithPadding * Math.PI) / 180;
    const chordLength = 2 * radius * Math.sin(arcRadians / 2);

    // Add extra space for stroke width and labels
    const requiredSize = chordLength + strokeWidth * 2 + 50;

    // ViewBox size: use required size directly, with min/max limits
    // Min: 80px (maximum zoom in for very short arcs)
    // Max: 400px (no zoom out beyond original view)
    const newWidth = Math.max(80, Math.min(requiredSize, 400));
    const newHeight = Math.max(80, Math.min(requiredSize, 400));

    // Calculate top-left corner of zoomed viewBox (centered on the sleep arc)
    const newX = centerPos.x - newWidth / 2;
    const newY = centerPos.y - newHeight / 2;

    return `${newX} ${newY} ${newWidth} ${newHeight}`;
  };

  // Handle sleep arc click - zoom in and open edit dialog
  const handleSleepArcClick = (entry: SleepEntry, event: React.MouseEvent) => {
    // Stop propagation to prevent background click from firing
    event.stopPropagation();

    // Zoom into this entry
    setZoomedEntry(entry);
    const newViewBox = calculateZoomViewBox(entry);
    setViewBox(newViewBox);

    // Also trigger the edit callback
    if (onSleepClick) {
      onSleepClick(entry);
    }
  };

  // Handle background click to zoom out
  const handleBackgroundClick = () => {
    if (zoomedEntry) {
      setZoomedEntry(null);
      setViewBox("0 0 400 400");
    }
  };

  // Format time without confidence qualifiers (for embedded contexts like "Wake up..." or "Should be sleeping until...")
  const formatTimeWithoutQualifier = (dateString: string, confidence: number): string => {
    const date = new Date(dateString);

    // Determine certainty level for rounding
    let certaintyLevel: 1 | 2 | 3;
    if (confidence < 0.5) {
      certaintyLevel = 1;
    } else if (confidence < 0.8) {
      certaintyLevel = 2;
    } else {
      certaintyLevel = 3;
    }

    // Apply rounding based on certainty
    const roundedDate = new Date(date);
    if (certaintyLevel === 1) {
      // Round to nearest 15 minutes
      const minutes = roundedDate.getMinutes();
      const rounded = Math.round(minutes / 15) * 15;
      roundedDate.setMinutes(rounded, 0, 0);
    } else if (certaintyLevel === 2) {
      // Round to nearest 5 minutes
      const minutes = roundedDate.getMinutes();
      const rounded = Math.round(minutes / 5) * 5;
      roundedDate.setMinutes(rounded, 0, 0);
    }

    // Format time
    const now = new Date();
    const diffMinutes = Math.floor((roundedDate.getTime() - now.getTime()) / (1000 * 60));

    // For very soon times (within an hour)
    if (diffMinutes > 0 && diffMinutes < 60) {
      const roundingInterval = certaintyLevel === 1 ? 15 : 5;
      const roundedDiff = Math.round(diffMinutes / roundingInterval) * roundingInterval;
      return `in ${roundedDiff}m`;
    }

    // Standard time display
    const timeString = roundedDate.toLocaleTimeString([], {
      hour: use24h ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: !use24h
    });

    const today = new Date();
    if (roundedDate.getDate() === today.getDate()) {
      // Use "around" for low/medium certainty, nothing for high
      if (certaintyLevel === 3) {
        return timeString;
      } else {
        return `around ${timeString}`;
      }
    }

    // For tomorrow
    if (certaintyLevel === 3) {
      return `tomorrow at ${timeString}`;
    } else {
      return `tomorrow around ${timeString}`;
    }
  };

  // Format "should be sleeping" text with proper grammar for both relative and absolute times
  const formatSleepingUntilText = (dateString: string, confidence: number): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((date.getTime() - now.getTime()) / (1000 * 60));

    // For relative times (< 1 hour), use "for another XXm"
    if (diffMinutes > 0 && diffMinutes < 60) {
      const timeText = formatTimeWithoutQualifier(dateString, confidence);
      // timeText will be "in XXm", so we replace "in" with "for another"
      return `for another ${timeText.replace('in ', '')}`;
    }

    // For absolute times, use "until [time]"
    const timeText = formatTimeWithoutQualifier(dateString, confidence);
    return `until ${timeText}`;
  };

  return (
    <div className="sleep-wheel-container">
      <h3 className="sleep-wheel-title">
        Last {VISIBLE_HOURS} Hours
      </h3>
      {!zoomedEntry && (
        <p className="sleep-wheel-helper">Click a sleep entry to edit it</p>
      )}
      {zoomedEntry && (
        <p className="zoom-indicator">(Click background to zoom out)</p>
      )}

      <motion.svg
        viewBox={viewBox}
        className="sleep-wheel-svg"
        animate={{ viewBox }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
      >
        {/* Transparent overlay for catching background clicks when zoomed */}
        {zoomedEntry && (() => {
          // Parse viewBox to get current coordinates
          const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(' ').map(Number);
          return (
            <rect
              x={vbX}
              y={vbY}
              width={vbWidth}
              height={vbHeight}
              fill="transparent"
              onClick={handleBackgroundClick}
              style={{ cursor: 'pointer' }}
            />
          );
        })()}

        {/* Background arc/circle - show the visible time period */}
        {MARGIN_HOURS === 0 ? (
          // Full circle when there's no margin
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="transparent"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth="2"
            style={{ pointerEvents: 'none' }}
          />
        ) : (
          // Arc when there's a margin
          <>
            <path
              d={describeArc(centerX, centerY, radius, visibleArcStartAngle, visibleArcEndAngle)}
              fill="transparent"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="2"
              style={{ pointerEvents: 'none' }}
            />
            {/* Visible window start marker */}
            <line
              x1={polarToCartesian(centerX, centerY, radius - 8, visibleArcStartAngle).x}
              y1={polarToCartesian(centerX, centerY, radius - 8, visibleArcStartAngle).y}
              x2={polarToCartesian(centerX, centerY, radius + 8, visibleArcStartAngle).x}
              y2={polarToCartesian(centerX, centerY, radius + 8, visibleArcStartAngle).y}
              stroke="rgba(255, 255, 255, 0.2)"
              strokeWidth="2"
              style={{ pointerEvents: 'none' }}
            />
          </>
        )}

        {/* Hour markers */}
        {(() => {
          // Generate appropriate hour markers based on TOTAL_HOURS
          const markerCount = TOTAL_HOURS === 12 ? 12 : 8;
          const hourInterval = TOTAL_HOURS / markerCount;

          return Array.from({ length: markerCount }, (_, i) => {
            const hour = i * hourInterval;
            const angle = (hour / TOTAL_HOURS) * 360 + 270;
          const innerPos = polarToCartesian(centerX, centerY, radius - 10, angle);
          const outerPos = polarToCartesian(centerX, centerY, radius + 10, angle);
          const labelPos = polarToCartesian(centerX, centerY, radius + 35, angle);

          return (
            <g key={hour} style={{ pointerEvents: 'none' }}>
              {/* Hour marker line */}
              <line
                x1={innerPos.x}
                y1={innerPos.y}
                x2={outerPos.x}
                y2={outerPos.y}
                stroke="rgba(255, 255, 255, 0.3)"
                strokeWidth="2"
              />
              {/* Hour label */}
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="hour-label"
              >
                {formatHourLabel(hour)}
              </text>
            </g>
          );
          });
        })()}

        {/* Completed sleep arcs */}
        {todaysSleep.filter(entry => !entry.is_ongoing).map(entry => {
          const startTime = new Date(entry.start_time);
          const endTime = new Date(entry.end_time!);
          let startAngle = adjustStartAngle(timeToAngle(startTime));
          let endAngle = adjustEndAngle(timeToAngle(endTime), startAngle);
          const opacity = calculateOpacity(endTime);
          const isZoomed = zoomedEntry?.id === entry.id;

          // Calculate angular difference to detect short arcs
          let angleDiff = endAngle - startAngle;
          if (angleDiff < 0) angleDiff += 360;

          // Enforce minimum arc size for visibility (3 degrees minimum)
          const MIN_ARC_DEGREES = 3;
          if (angleDiff < MIN_ARC_DEGREES) {
            // Expand the arc symmetrically from its center
            const centerAngle = (startAngle + endAngle) / 2;
            const halfMinArc = MIN_ARC_DEGREES / 2;
            startAngle = centerAngle - halfMinArc;
            endAngle = centerAngle + halfMinArc;
            angleDiff = MIN_ARC_DEGREES;
          }

          // For short arcs, offset labels angularly to prevent overlap
          const isShortArc = angleDiff < 20;
          const baseLabelRadius = radius + 35;

          // For short arcs, position labels outside the arc along the circle
          const labelAngleOffset = 5; // degrees to offset labels from the arc
          const startLabelAngle = isShortArc ? startAngle - labelAngleOffset : startAngle;
          const endLabelAngle = isShortArc ? endAngle + labelAngleOffset : endAngle;

          // Calculate label positions (both at same radius, on the circle)
          const startLabelPos = polarToCartesian(centerX, centerY, baseLabelRadius, startLabelAngle);
          const endLabelPos = polarToCartesian(centerX, centerY, baseLabelRadius, endLabelAngle);

          return (
            <g key={entry.id}>
              <path
                d={describeArc(centerX, centerY, radius, startAngle, endAngle)}
                fill="transparent"
                stroke={isZoomed ? "#8f86ff" : "#7c73ff"}
                strokeWidth={isZoomed ? strokeWidth + 5 : strokeWidth}
                strokeLinecap="butt"
                className="sleep-arc completed clickable"
                onClick={(e) => handleSleepArcClick(entry, e)}
                style={{ opacity: isZoomed ? 1 : opacity, cursor: 'pointer' }}
              >
                <title>{`Sleep: ${formatDuration(entry.duration_minutes)}`}</title>
              </path>

              {/* Time labels - only show when zoomed */}
              {isZoomed && (
                <>
                  {/* Start time label */}
                  <g style={{ pointerEvents: 'none' }}>
                    <rect
                      x={startLabelPos.x - 25}
                      y={startLabelPos.y - 10}
                      width="50"
                      height="20"
                      fill="rgba(0, 0, 0, 0.7)"
                      rx="4"
                    />
                    <text
                      x={startLabelPos.x}
                      y={startLabelPos.y + 4}
                      textAnchor="middle"
                      fontSize="11"
                      fill="#ffffff"
                      fontWeight="500"
                    >
                      {formatTimeLabel(startTime)}
                    </text>
                  </g>

                  {/* End time label */}
                  <g style={{ pointerEvents: 'none' }}>
                    <rect
                      x={endLabelPos.x - 25}
                      y={endLabelPos.y - 10}
                      width="50"
                      height="20"
                      fill="rgba(0, 0, 0, 0.7)"
                      rx="4"
                    />
                    <text
                      x={endLabelPos.x}
                      y={endLabelPos.y + 4}
                      textAnchor="middle"
                      fontSize="11"
                      fill="#ffffff"
                      fontWeight="500"
                    >
                      {formatTimeLabel(endTime)}
                    </text>
                  </g>
                </>
              )}
            </g>
          );
        })}

        {/* Ongoing sleep arc */}
        {ongoingSleep && (() => {
          const startTime = new Date(ongoingSleep.start_time);
          let startAngle = adjustStartAngle(timeToAngle(startTime));
          const isZoomed = zoomedEntry?.id === ongoingSleep.id;

          // Calculate angular difference to detect short arcs
          let angleDiff = currentAngle - startAngle;
          if (angleDiff < 0) angleDiff += 360;

          // Enforce minimum arc size for visibility (3 degrees minimum)
          const MIN_ARC_DEGREES = 3;
          if (angleDiff < MIN_ARC_DEGREES) {
            // Expand the arc backward from the current time
            startAngle = currentAngle - MIN_ARC_DEGREES;
            angleDiff = MIN_ARC_DEGREES;
          }

          // For short arcs, offset label angularly to prevent overlap with current time indicator
          const isShortArc = angleDiff < 20;
          const baseLabelRadius = radius + 35;

          // For short arcs, position label before the arc along the circle
          const labelAngleOffset = 5; // degrees to offset label from the arc
          const startLabelAngle = isShortArc ? startAngle - labelAngleOffset : startAngle;

          // Calculate label position for start time (on the circle)
          const startLabelPos = polarToCartesian(centerX, centerY, baseLabelRadius, startLabelAngle);

          return (
            <g>
              <path
                d={describeArc(centerX, centerY, radius, startAngle, currentAngle)}
                fill="transparent"
                stroke="#8f86ff"
                strokeWidth={isZoomed ? strokeWidth + 5 : strokeWidth}
                strokeLinecap="butt"
                className="sleep-arc ongoing clickable"
                onClick={(e) => handleSleepArcClick(ongoingSleep, e)}
                style={{ cursor: 'pointer' }}
              />

              {/* Time label - only show when zoomed */}
              {isZoomed && (
                <g style={{ pointerEvents: 'none' }}>
                  <rect
                    x={startLabelPos.x - 25}
                    y={startLabelPos.y - 10}
                    width="50"
                    height="20"
                    fill="rgba(0, 0, 0, 0.7)"
                    rx="4"
                  />
                  <text
                    x={startLabelPos.x}
                    y={startLabelPos.y + 4}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#ffffff"
                    fontWeight="500"
                  >
                    {formatTimeLabel(startTime)}
                  </text>
                </g>
              )}
            </g>
          );
        })()}

        {/* Current time indicator line */}
        <line
          x1={polarToCartesian(centerX, centerY, radius - 8, currentAngle).x}
          y1={polarToCartesian(centerX, centerY, radius - 8, currentAngle).y}
          x2={polarToCartesian(centerX, centerY, radius + 8, currentAngle).x}
          y2={polarToCartesian(centerX, centerY, radius + 8, currentAngle).y}
          stroke="#ffdf5d"
          strokeWidth="2"
          className="current-time-indicator"
          style={{ pointerEvents: 'none' }}
        />

        {/* Center content - total sleep */}
        <g
          onClick={handleBackgroundClick}
          style={{ cursor: zoomedEntry ? 'pointer' : 'default' }}
        >
          <text
            x={centerX}
            y={centerY - 15}
            textAnchor="middle"
            className="center-label"
          >
            Total Sleep
          </text>
          <text
            x={centerX}
            y={centerY + 15}
            textAnchor="middle"
            className="center-value"
          >
            {formatTotalSleep(totalSleepMinutes)}
          </text>
        </g>
      </motion.svg>

      {/* Next Predicted Sleep or Loading */}
      {(isLoadingPrediction || nextPrediction) && (
        <div
          className="wheel-prediction"
          onClick={onPredictionClick}
        >
          {isLoadingPrediction ? (
            <>
              <span className="prediction-icon">💭</span>
              <span className="prediction-text">Calculating sleep schedule</span>
            </>
          ) : nextPrediction && (
            <>
              {nextPrediction.displayMode === 'waking' && (
                <>
                  <span className="prediction-icon">☀️</span>
                  <span className="prediction-text">
                    Wake up {formatTimeWithoutQualifier(
                      nextPrediction.slot.predicted_end_time,
                      nextPrediction.slot.confidence_score || 0.5
                    )}
                  </span>
                </>
              )}

              {nextPrediction.displayMode === 'should-be-sleeping' && (
                <>
                  <span className="prediction-icon">😴</span>
                  <span className="prediction-text">
                    Should be sleeping {formatSleepingUntilText(
                      nextPrediction.slot.predicted_end_time,
                      nextPrediction.slot.confidence_score || 0.5
                    )}
                  </span>
                </>
              )}

              {nextPrediction.displayMode === 'future' && (
                <>
                  <span className="prediction-icon">💤</span>
                  <span className="prediction-text">
                    Next sleep {formatTimeWithoutQualifier(
                      nextPrediction.slot.predicted_start_time,
                      nextPrediction.slot.confidence_score || 0.5
                    )}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SleepWheel;
